import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { useUpdateCouponMutation } from '@/store/api/services/marketing/coupons';
import type { Coupon } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const COUPON_SVG_MAX_SIZE_MB = 2;

type EditCouponProps = {
  coupon?: Coupon;
};

/** Only the fields Unity's `UpdateCouponData` (PUT /coupons/{id}) actually accepts — code,
 * discount, coupon_type, and customer assignment are immutable once a coupon is created. */
const EditCoupon = ({ hide, props }: ModalChildrenProps) => {
  const { coupon } = (props ?? {}) as EditCouponProps;
  const [updateCoupon, { isLoading }] = useUpdateCouponMutation();

  const [title, setTitle] = useState(coupon?.title ?? '');
  const [description, setDescription] = useState(coupon?.description ?? '');
  const [startDate, setStartDate] = useState(coupon?.start_date?.slice(0, 16) ?? '');
  const [expiryDate, setExpiryDate] = useState(coupon?.expiry_date?.slice(0, 16) ?? '');
  const [usageLimit, setUsageLimit] = useState(coupon?.usage_limit?.toString() ?? '');
  const [autoAssignOnCheck, setAutoAssignOnCheck] = useState(coupon?.auto_assign_on_check ?? false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDateOrderValid = !startDate || !expiryDate || startDate < expiryDate;
  const isValid = isDateOrderValid;

  if (!coupon) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (!isSvg) {
      setImageError('Only SVG files are accepted');
      return;
    }

    if (file.size > COUPON_SVG_MAX_SIZE_MB * 1024 * 1024) {
      setImageError(`File size can not exceed ${COUPON_SVG_MAX_SIZE_MB}MB`);
      return;
    }

    setImageError('');
    setImageFile(file);
  };

  const handleFileRemove = () => {
    setImageFile(null);
    setImageError('');
  };

  const handleSubmit = async () => {
    try {
      await updateCoupon({
        couponId: coupon.coupon_id,
        title: title.trim() || null,
        description: description.trim() || null,
        start_date: startDate ? new Date(startDate).toISOString() : null,
        expiry_date: expiryDate ? new Date(expiryDate).toISOString() : null,
        usage_limit: usageLimit ? Number(usageLimit) : null,
        auto_assign_on_check: autoAssignOnCheck,
        file: imageFile ?? undefined,
      }).unwrap();
      toast.success('Coupon updated successfully');
      hide();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to update coupon';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack gap={3} py={2} data-test="edit-coupon-form">
          <TextField label="Code" value={coupon.code} fullWidth disabled />

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            inputProps={{ 'data-test': 'edit-coupon-title-input' }}
          />

          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ 'data-test': 'edit-coupon-description-input' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '90px',
                },
              }}
            />
          </Stack>

          {coupon.coupon_type === 'individual_coupon' && (
            <FormControlLabel
              control={
                <Switch
                  checked={autoAssignOnCheck}
                  onChange={(e) => setAutoAssignOnCheck(e.target.checked)}
                />
              }
              label="Auto-assign when a customer meets criteria"
            />
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Optional settings
          </Typography>

          <Stack direction="row" gap={2}>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              error={!isDateOrderValid}
              helperText={!isDateOrderValid ? 'Must be before the expiry date' : ' '}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Expiry Date & Time"
              type="datetime-local"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            label="Usage Limit"
            type="number"
            value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
            helperText="Leave empty for unlimited"
          />

          <Stack gap={1}>
            <Typography variant="body2">Image</Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,image/svg+xml"
              hidden
              onChange={handleFileSelect}
            />

            {imageFile ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="body2" noWrap>
                  {imageFile.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleFileRemove}
                  data-test="edit-coupon-image-remove"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ) : (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                data-test="edit-coupon-image-upload"
              >
                {coupon.image_url ? 'Replace SVG' : 'Upload SVG'}
              </Button>
            )}

            {imageError && <FormHelperText error>{imageError}</FormHelperText>}
          </Stack>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={isLoading}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default EditCoupon;
